/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useState } from 'react';

const ADVANCED_VIEW_ENABLED_STORAGE_KEY = 'advanced-view-enabled';
const DEBUG_SECTION_ENABLED_STORAGE_KEY = 'debug-section-enabled';
const DEMO_SECTION_ENABLED_STORAGE_KEY = 'demo-section-enabled';
const TOOLS_SECTION_ENABLED_STORAGE_KEY = 'tools-section-enabled';
const THEME_STORAGE_KEY = 'theme';
const TECHNICAL_DETAILS_ENABLED_STORAGE_KEY = 'technical-details-enabled';

export type AppTheme = 'dark' | 'light';

function readStoredBooleanPreference(storageKey: string): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    return window.localStorage.getItem(storageKey) === 'true';
}

function readStoredTheme(): AppTheme {
    if (typeof window === 'undefined') {
        return 'light';
    }

    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);

    return storedTheme === 'dark' || storedTheme === 'light'
        ? storedTheme
        : 'light';
}

/**
 * Persists user-controlled presentation preferences and keeps the daisyUI
 * theme attribute synchronized with the active theme.
 */
export function useAppPreferences() {
    const [theme, setTheme] = useState<AppTheme>(readStoredTheme);
    const [isDemoSectionEnabled, setIsDemoSectionEnabled] = useState(() => (
        readStoredBooleanPreference(DEMO_SECTION_ENABLED_STORAGE_KEY)
    ));
    const [isDebugSectionEnabled, setIsDebugSectionEnabled] = useState(() => (
        readStoredBooleanPreference(DEBUG_SECTION_ENABLED_STORAGE_KEY)
    ));
    const [isToolsSectionEnabled, setIsToolsSectionEnabled] = useState(() => (
        readStoredBooleanPreference(TOOLS_SECTION_ENABLED_STORAGE_KEY)
    ));
    const [isAdvancedViewEnabled, setIsAdvancedViewEnabled] = useState(() => (
        readStoredBooleanPreference(ADVANCED_VIEW_ENABLED_STORAGE_KEY)
    ));
    const [isTechnicalDetailsEnabled, setIsTechnicalDetailsEnabled] = useState(() => (
        readStoredBooleanPreference(TECHNICAL_DETAILS_ENABLED_STORAGE_KEY)
    ));

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }, [theme]);

    useEffect(() => {
        window.localStorage.setItem(
            DEMO_SECTION_ENABLED_STORAGE_KEY,
            String(isDemoSectionEnabled),
        );
    }, [isDemoSectionEnabled]);

    useEffect(() => {
        window.localStorage.setItem(
            DEBUG_SECTION_ENABLED_STORAGE_KEY,
            String(isDebugSectionEnabled),
        );
    }, [isDebugSectionEnabled]);

    useEffect(() => {
        window.localStorage.setItem(
            TOOLS_SECTION_ENABLED_STORAGE_KEY,
            String(isToolsSectionEnabled),
        );
    }, [isToolsSectionEnabled]);

    useEffect(() => {
        window.localStorage.setItem(
            ADVANCED_VIEW_ENABLED_STORAGE_KEY,
            String(isAdvancedViewEnabled),
        );
    }, [isAdvancedViewEnabled]);

    useEffect(() => {
        window.localStorage.setItem(
            TECHNICAL_DETAILS_ENABLED_STORAGE_KEY,
            String(isTechnicalDetailsEnabled),
        );
    }, [isTechnicalDetailsEnabled]);

    return {
        isAdvancedViewEnabled,
        isDebugSectionEnabled,
        isDemoSectionEnabled,
        isTechnicalDetailsEnabled,
        isToolsSectionEnabled,
        setIsAdvancedViewEnabled,
        setIsDebugSectionEnabled,
        setIsDemoSectionEnabled,
        setIsTechnicalDetailsEnabled,
        setIsToolsSectionEnabled,
        setTheme,
        theme,
    };
}
